import { requireRecycleSuccess } from '@/lib/recycleErrors';
// ─────────────────────────────────────────────
// KLOTHIQ — useSales hook
// ─────────────────────────────────────────────

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { refreshSaleConsumers } from '@/lib/refreshSaleConsumers';
import type { SalePaymentDraft } from '@klothiq/validation';
import type { Database, SaleFinancialSummary } from '@klothiq/domain';
import { fetchAllSales, salesRpc } from '@/lib/salesOverview';

export type Sale = Database['public']['Tables']['sales']['Row'];
export type SaleItem = Database['public']['Tables']['sale_items']['Row'];

export interface SalePaymentSummary {
  id?: string;
  payment_method: string;
  amount?: number;
  is_void?: boolean;
  payment_date?: string;
}

export interface SaleWithCustomer extends Sale {
  customers: { name: string; phone: string | null } | null;
  payments?: SalePaymentSummary[];
  entered_by_name?: string | null;
  item_count?: number;
  returned_units?: number;
  financial_summary?: SaleFinancialSummary;
  is_deleted?: boolean;
  deleted_at?: string | null;
  delete_reason?: string | null;
}

export const SALES_KEY = 'sales';
export const RECENT_SALES_KEY = 'recent_sales';

// ── List sales ────────────────────────────────────────────────
export function useSales(filters?: { status?: string; search?: string }) {
  const { organization, profile, access, can } = useAuth();
  const orgId = organization?.id;

  return useQuery({
    queryKey: [SALES_KEY, orgId, profile?.id, access?.access_version, filters],
    queryFn: () =>
      fetchAllSales<SaleWithCustomer>({ status: filters?.status, search: filters?.search?.trim() }),
    enabled: !!orgId && can('sales.view'),
    staleTime: 1000 * 30,
    refetchInterval: 60000,
  });
}

// ── Single sale with items ────────────────────────────────────
export function useSale(saleId?: string) {
  const { organization, profile, access, can } = useAuth();
  const orgId = organization?.id;

  return useQuery({
    queryKey: [SALES_KEY, orgId, profile?.id, access?.access_version, saleId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('sales')
        .select(
          `
          *,
          customers(id, name, phone, address, city, pincode),
          sale_items(
            *,
            product_variants(sku, size, color, default_price, products(name, product_code))
          ),
          payments(id, amount, payment_method, payment_date, payment_type, account_id, reference_number, notes, created_at, updated_at, is_void, cash_accounts(id, name))
        `,
        )
        .eq('id', saleId!)
        .eq('organization_id', orgId!)
        .single();
      if (error) throw error;
      if (data.is_voided || data.is_deleted) return data;
      const overview = await salesRpc<SaleWithCustomer>('get_sale_overview_row', {
        p_sale_id: saleId,
      });
      return {
        ...data,
        seller_name: overview.seller_name,
        paid_amount: overview.paid_amount,
        payment_status: overview.payment_status,
        entered_by_name: overview.entered_by_name,
        financial_summary: overview.financial_summary,
      };
    },
    enabled: !!orgId && !!saleId && can('sales.view'),
    refetchInterval: 60000,
  });
}

// ── Create/Update sale via RPC ────────────────────────────────
export interface SaleItemInput {
  variant_id: string;
  quantity: number;
  unit_price: number;
  discount?: number;
}

export interface SaleInput {
  fulfillment_method?: import('@/lib/deliveries').FulfillmentMethod;
  request_key?: string;
  payment?: SalePaymentDraft & { collect_remaining?: boolean };
  payment_edits?: Array<SalePaymentDraft & { payment_id: string; expected_updated_at?: string }>;
  sale_id?: string;
  expected_updated_at?: string; // FEAT-SALE-EDIT-002: optimistic concurrency
  customer_id?: string | null;
  customer_name?: string | null; // snapshot — preserve exact saved values on unrelated edits
  customer_mobile?: string | null; // snapshot — from selected customer (overridable) or manual entry
  customer_address?: string | null; // snapshot — same rationale as customer_mobile
  sold_by?: string; // profiles.id — exactly one of sold_by / sold_by_staff_id is required
  sold_by_staff_id?: string; // staff_members.id
  seller_name?: string; // snapshot of the seller's display name at time of sale
  sale_date: string;
  sale_status?: string;
  delivery_charge?: number;
  notes?: string;
  items: SaleItemInput[];
}

export function useCreateOrUpdateSale() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: SaleInput) => {
      const { data, error } = await supabase.rpc('create_or_update_sale', {
        p_data: input,
      });
      if (error) {
        console.error('[create_or_update_sale] Supabase RPC error:', error);
        const err = new Error(error.message || 'Failed to save sale');
        Object.assign(err, error);
        throw err;
      }
      return data as { sale_id: string; success: boolean };
    },
    onSuccess: () => refreshSaleConsumers(queryClient),
  });
}

// ── FEAT-SALE-METADATA-003: Update sale customer metadata & notes ──
export interface UpdateSaleMetadataInput {
  sale_id: string;
  expected_updated_at?: string;
  customer_id?: string | null;
  customer_name: string;
  customer_mobile?: string | null;
  customer_address?: string | null;
  notes?: string | null;
}

export function useUpdateSaleMetadata() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: UpdateSaleMetadataInput) => {
      const { data, error } = await supabase.rpc('update_sale_metadata', { p_data: input });
      if (error) {
        console.error('[update_sale_metadata] Supabase RPC error:', error);
        const err = new Error(error.message || 'Failed to update customer details');
        Object.assign(err, error);
        throw err;
      }
      return data as { sale_id: string; success: boolean; updated_at: string };
    },
    onSuccess: () => refreshSaleConsumers(queryClient),
  });
}

// ── Change sale status via RPC ────────────────────────────────
export function useChangeSaleStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: { sale_id: string; new_status: string; reason?: string }) => {
      const { data, error } = await supabase.rpc('change_sale_status', { p_data: input });
      if (error) throw error;
      return data as { sale_id: string; new_status: string; success: boolean };
    },
    onSuccess: () => refreshSaleConsumers(queryClient),
  });
}

// ── Return workflow: request -> accept/reject -> complete ──────
// FEAT-SALE-STATUS-001. Three independent steps wrapping the
// previously-orphaned process_sale_return RPC — see spec for why this
// isn't just a bigger sale_status enum.

export interface RequestSaleReturnInput {
  sale_id: string;
  items: Array<{ sale_item_id: string; quantity: number }>;
  reason: string;
}

export function useRequestSaleReturn() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: RequestSaleReturnInput) => {
      const { data, error } = await supabase.rpc('request_sale_return', { p_data: input });
      if (error) {
        console.error('[request_sale_return] Supabase RPC error:', error);
        const err = new Error(error.message || 'Failed to request return');
        Object.assign(err, error);
        throw err;
      }
      return data as { success: boolean; return_status: string };
    },
    onSuccess: () => refreshSaleConsumers(queryClient),
  });
}

export interface DecideSaleReturnInput {
  sale_id: string;
  decision: 'accepted' | 'rejected';
  note?: string;
}

export function useDecideSaleReturn() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: DecideSaleReturnInput) => {
      const { data, error } = await supabase.rpc('decide_sale_return', { p_data: input });
      if (error) {
        console.error('[decide_sale_return] Supabase RPC error:', error);
        const err = new Error(error.message || 'Failed to record decision');
        Object.assign(err, error);
        throw err;
      }
      return data as { success: boolean; return_status: string };
    },
    onSuccess: () => refreshSaleConsumers(queryClient),
  });
}

export interface CompleteSaleReturnInput {
  sale_id: string;
  items: Array<{ sale_item_id: string; condition: 'good' | 'damaged'; restock: boolean }>;
  refund_account_id?: string;
  refund_amount?: number;
}

export function useCompleteSaleReturn() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CompleteSaleReturnInput) => {
      const { data, error } = await supabase.rpc('complete_sale_return', { p_data: input });
      if (error) {
        console.error('[complete_sale_return] Supabase RPC error:', error);
        const err = new Error(error.message || 'Failed to complete return');
        Object.assign(err, error);
        throw err;
      }
      return data as { success: boolean; return_status: string; items_returned: number };
    },
    onSuccess: () => refreshSaleConsumers(queryClient),
  });
}

// ── Record payment via RPC ─────────────────────────────────────
export function useRecordPayment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: {
      request_key?: string;
      duplicate_confirmed?: boolean;
      collect_remaining?: boolean;
      payment_type: 'sale_payment' | 'purchase_payment' | 'refund';
      sale_id?: string;
      purchase_id?: string;
      account_id: string;
      amount: number;
      payment_method: string;
      payment_date: string;
      reference_number?: string;
      notes?: string;
    }) => {
      const { data, error } = await supabase.rpc('record_payment', { p_data: input });
      if (error) throw error;
      return data as { payment_id: string; cash_txn_id: string; success: boolean };
    },
    onSuccess: () => refreshSaleConsumers(queryClient),
  });
}

// ── Update sale payment via RPC ─────────────────────────────────
export interface UpdateSalePaymentInput {
  request_key?: string;
  expected_updated_at?: string;
  reason?: string;
  payment_id: string;
  sale_id: string;
  amount: number;
  account_id: string;
  payment_method: string;
  payment_date: string;
  reference_number?: string;
  notes?: string;
}

export function useUpdateSalePayment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: UpdateSalePaymentInput) => {
      const { data, error } = await supabase.rpc('update_sale_payment', { p_data: input });
      if (error) {
        console.error('[update_sale_payment] Supabase RPC error:', error);
        if (error.code === 'PGRST202' || error.message?.includes('schema cache')) {
          throw new Error(
            'Payment correction is unavailable. Apply migration 00048_sale_payment_lifecycle.sql before using this editor.',
          );
        }
        throw new Error(error.message || 'Failed to update payment details');
      }
      return data as {
        success: boolean;
        payment_id: string;
        sale_id: string;
        amount: number;
        total_paid: number;
      };
    },
    onSuccess: () => refreshSaleConsumers(queryClient),
  });
}

// ── Void sale payment via RPC ───────────────────────────────────
export interface VoidSalePaymentInput {
  request_key?: string;
  expected_updated_at?: string;
  payment_id: string;
  sale_id: string;
  reason?: string;
}

export function useVoidSalePayment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: VoidSalePaymentInput) => {
      const { data, error } = await supabase.rpc('void_sale_payment', { p_data: input });
      if (error) {
        console.error('[void_sale_payment] Supabase RPC error:', error);
        throw new Error(error.message || 'Failed to void payment');
      }
      return data as { success: boolean; payment_id: string; total_paid: number };
    },
    onSuccess: () => refreshSaleConsumers(queryClient),
  });
}

// ── Query: deleted sales (for Recycle Bin) ─────────────────────
export function useDeletedSales() {
  const { organization } = useAuth();
  const orgId = organization?.id;

  return useQuery({
    queryKey: [SALES_KEY, 'deleted', orgId],
    queryFn: async (): Promise<SaleWithCustomer[]> => {
      const { data, error } = await supabase
        .from('sales')
        .select(`*, customers(name, phone)`)
        .eq('organization_id', orgId!)
        .eq('is_deleted', true)
        .order('deleted_at', { ascending: false });
      if (error) {
        if (error.message?.includes('is_deleted') || error.code === 'PGRST204') return [];
        throw error;
      }
      return (data as SaleWithCustomer[]) ?? [];
    },
    enabled: !!orgId,
    staleTime: 1000 * 15,
  });
}

// ── Mutation: soft delete sale (move to recycle bin) ───────────
export function useSoftDeleteSale() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: { sale_id: string; reason?: string }) => {
      const { data, error } = await supabase.rpc('soft_delete_sale', { p_data: params });
      requireRecycleSuccess(data, error);
      return data;
    },
    onSuccess: () => refreshSaleConsumers(queryClient),
  });
}

// ── Mutation: restore sale from recycle bin ────────────────────
export function useRestoreSale() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: { sale_id: string }) => {
      const { data, error } = await supabase.rpc('restore_sale', { p_data: params });
      requireRecycleSuccess(data, error);
      return data;
    },
    onSuccess: () => refreshSaleConsumers(queryClient),
  });
}

// ── Mutation: permanently delete sale (hard purge) ─────────────
export function usePermanentDeleteSale() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: { sale_id: string }) => {
      const { data, error } = await supabase.rpc('permanently_delete_sale', { p_data: params });
      requireRecycleSuccess(data, error);
      return data;
    },
    onSuccess: () => refreshSaleConsumers(queryClient),
  });
}

// ── Sale Tracking (FEAT-SALE-STATUS-003) ───────────────────────
// Reads the existing audit_logs trail for one sale — no new table.
// Every status-changing RPC (change_sale_status, request/decide/
// complete_sale_return) already calls write_audit_log() against
// table_name='sales'. Payment history is intentionally NOT included
// here (record_payment audits table_name='payments', and the Sale
// Detail page already has its own Payment History section).
export interface SaleTrackingEntry {
  id: string;
  action: string;
  old_data: Record<string, unknown> | null;
  new_data: Record<string, unknown> | null;
  metadata: Record<string, unknown> | null;
  created_at: string;
  performed_by: string | null;
  actor_name: string | null;
}

export function useSaleTracking(saleId?: string) {
  const { organization } = useAuth();
  const orgId = organization?.id;

  return useQuery({
    queryKey: ['sale_tracking', orgId, saleId],
    queryFn: async (): Promise<SaleTrackingEntry[]> => {
      const { data, error } = await supabase
        .from('audit_logs')
        .select(
          'id, action, old_data, new_data, metadata, created_at, performed_by, profiles(full_name)',
        )
        .eq('organization_id', orgId!)
        .eq('table_name', 'sales')
        .eq('record_id', saleId!)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (data ?? []).map((row) => {
        const profile = row.profiles as unknown as
          { full_name: string } | { full_name: string }[] | null;
        const actor_name = Array.isArray(profile)
          ? (profile[0]?.full_name ?? null)
          : (profile?.full_name ?? null);
        return {
          id: row.id,
          action: row.action,
          old_data: row.old_data as Record<string, unknown> | null,
          new_data: row.new_data as Record<string, unknown> | null,
          metadata: row.metadata as Record<string, unknown> | null,
          created_at: row.created_at,
          performed_by: row.performed_by,
          actor_name,
        };
      });
    },
    enabled: !!orgId && !!saleId,
    staleTime: 1000 * 30,
  });
}
